import { createNoise2D, type NoiseFunction2D } from 'simplex-noise';
import { clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { RenderTarget, TechniqueContext } from '../types';
import type { ContourLinesValues } from './params';

/**
 * Topographic contours over a height field built from the text.
 *
 * The height field mixes the signed distance with fractal noise, so at `noiseMix = 0`
 * you get clean offset rings around the letterforms and at `1` a landscape that merely
 * happens to be shaped by them.
 *
 * Contours come from marching squares. The linear interpolation along each cell edge is
 * what makes the result look like a drawn contour rather than a staircase — without it
 * the lines would snap to the sampling grid.
 */
export interface ContourLinesState {
  /** Height field, sampled on a coarse grid. */
  heights: Float32Array;
  cols: number;
  rows: number;
  cell: number;
  min: number;
  max: number;
  /** Per-level polyline segments as flat [x0,y0,x1,y1, ...]. */
  levels: Float32Array[];
  palette: ResolvedPalette;
  levelsBuilt: number;
  fieldBuilt: boolean;
  built: boolean;
  noise: NoiseFunction2D;
  warpNoise: NoiseFunction2D;
}

function values(ctx: TechniqueContext): ContourLinesValues {
  return ctx.values as unknown as ContourLinesValues;
}

function fbm(noise: NoiseFunction2D, x: number, y: number, octaves: number): number {
  let sum = 0;
  let amp = 1;
  let freq = 1;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += noise(x * freq, y * freq) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return norm > 0 ? sum / norm : 0;
}

export function init(ctx: TechniqueContext): ContourLinesState {
  const v = values(ctx);
  const cell = Math.max(1, v.resolution);
  const cols = Math.ceil(ctx.width / cell) + 1;
  const rows = Math.ceil(ctx.height / cell) + 1;

  return {
    heights: new Float32Array(cols * rows),
    cols,
    rows,
    cell,
    min: 0,
    max: 1,
    levels: [],
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    levelsBuilt: 0,
    fieldBuilt: false,
    built: false,
    noise: createNoise2D(() => ctx.rng.next()),
    warpNoise: createNoise2D(() => ctx.rng.next()),
  };
}

function buildField(state: ContourLinesState, v: ContourLinesValues, ctx: TechniqueContext): void {
  const { cols, rows, cell } = state;
  const scale = v.noiseScale;
  let min = Infinity;
  let max = -Infinity;
  const reach = Math.max(1, ctx.field.maxOutside);

  for (let gy = 0; gy < rows; gy++) {
    for (let gx = 0; gx < cols; gx++) {
      let x = gx * cell;
      let y = gy * cell;

      if (v.warp > 0) {
        x += fbm(state.warpNoise, x * scale * 0.6, y * scale * 0.6, 2) * v.warp;
        y += fbm(state.warpNoise, (x + 913) * scale * 0.6, (y - 447) * scale * 0.6, 2) * v.warp;
      }

      // Normalise the distance so the two contributions are on comparable scales;
      // otherwise the noise term is invisible next to a field that runs to 500 px.
      const sdf = clamp01((ctx.field.distance(x, y) + reach) / (reach * 2));
      const noise = (fbm(state.noise, x * scale, y * scale, v.octaves) + 1) / 2;
      const h = sdf * (1 - v.noiseMix) + noise * v.noiseMix;

      state.heights[gy * cols + gx] = h;
      if (h < min) min = h;
      if (h > max) max = h;
    }
  }

  state.min = min;
  state.max = max === min ? min + 1 : max;
  state.fieldBuilt = true;
}

/**
 * Marching squares for one iso-level.
 *
 * The case index is the four corners above/below the threshold; each case maps to zero,
 * one or two segments. Ambiguous saddles (cases 5 and 10) are resolved consistently
 * rather than by the cell centre, which is enough here and avoids a second field read.
 */
function marchLevel(state: ContourLinesState, threshold: number): Float32Array {
  const { heights, cols, rows, cell } = state;
  const out: number[] = [];

  const lerp = (a: number, b: number): number => (Math.abs(b - a) < 1e-9 ? 0.5 : (threshold - a) / (b - a));

  for (let gy = 0; gy < rows - 1; gy++) {
    for (let gx = 0; gx < cols - 1; gx++) {
      const tl = heights[gy * cols + gx];
      const tr = heights[gy * cols + gx + 1];
      const br = heights[(gy + 1) * cols + gx + 1];
      const bl = heights[(gy + 1) * cols + gx];

      const code = (tl > threshold ? 8 : 0) | (tr > threshold ? 4 : 0) | (br > threshold ? 2 : 0) | (bl > threshold ? 1 : 0);
      if (code === 0 || code === 15) continue;

      const x = gx * cell;
      const y = gy * cell;
      const top = { x: x + lerp(tl, tr) * cell, y };
      const right = { x: x + cell, y: y + lerp(tr, br) * cell };
      const bottom = { x: x + lerp(bl, br) * cell, y: y + cell };
      const left = { x, y: y + lerp(tl, bl) * cell };

      const push = (a: { x: number; y: number }, b: { x: number; y: number }): void => {
        out.push(a.x, a.y, b.x, b.y);
      };

      switch (code) {
        case 1:
        case 14:
          push(left, bottom);
          break;
        case 2:
        case 13:
          push(bottom, right);
          break;
        case 3:
        case 12:
          push(left, right);
          break;
        case 4:
        case 11:
          push(top, right);
          break;
        case 6:
        case 9:
          push(top, bottom);
          break;
        case 7:
        case 8:
          push(left, top);
          break;
        case 5:
          push(left, top);
          push(bottom, right);
          break;
        case 10:
          push(left, bottom);
          push(top, right);
          break;
      }
    }
  }

  return Float32Array.from(out);
}

export function step(state: ContourLinesState, _dt: number, ctx: TechniqueContext): ContourLinesState {
  if (state.built) return state;
  const v = values(ctx);

  if (!state.fieldBuilt) {
    buildField(state, v, ctx);
    return state;
  }

  // A few levels per frame keeps a 200-level setting responsive.
  const perFrame = Math.max(1, Math.round(6 * clamp01(ctx.quality)));
  for (let i = 0; i < perFrame && state.levelsBuilt < v.levels; i++) {
    const t = (state.levelsBuilt + 0.5) / v.levels;
    state.levels.push(marchLevel(state, state.min + (state.max - state.min) * t));
    state.levelsBuilt++;
  }

  if (state.levelsBuilt >= v.levels) state.built = true;
  return state;
}

export function isComplete(state: ContourLinesState): boolean {
  return state.built;
}

export function draw(state: ContourLinesState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  c.lineWidth = v.lineWidth;
  c.lineCap = 'round';

  for (let i = 0; i < state.levels.length; i++) {
    const segments = state.levels[i];
    if (segments.length === 0) continue;
    const t = state.levels.length > 1 ? i / (state.levels.length - 1) : 0;

    const path = new Path2D();
    for (let k = 0; k < segments.length; k += 4) {
      const x0 = segments[k];
      const y0 = segments[k + 1];
      if (v.clip !== 'all') {
        const signed = ctx.field.distance(x0, y0);
        if (v.clip === 'outside' && signed < 0) continue;
        if (v.clip === 'inside' && signed > 0) continue;
      }
      path.moveTo(x0, y0);
      path.lineTo(segments[k + 2], segments[k + 3]);
    }

    if (v.fill) {
      // Filled bands read as a stepped relief; the stroke width becomes the band edge.
      c.strokeStyle = state.palette.at(t, v.opacity);
      c.lineWidth = Math.max(v.lineWidth, state.cell * 1.6);
      c.stroke(path);
      c.lineWidth = v.lineWidth;
    } else {
      c.strokeStyle = state.palette.at(t, v.opacity);
      c.stroke(path);
    }
  }
}

export function elementCount(state: ContourLinesState): number {
  let n = 0;
  for (const level of state.levels) n += level.length / 4;
  return n;
}

export function toSVG(state: ContourLinesState, ctx: TechniqueContext): string {
  const v = values(ctx);
  const f = (n: number): string => n.toFixed(1).replace(/\.?0+$/, '');
  const parts: string[] = [`<g fill="none" stroke-width="${v.lineWidth}" stroke-opacity="${v.opacity}" stroke-linecap="round">`];

  for (let i = 0; i < state.levels.length; i++) {
    const segments = state.levels[i];
    if (segments.length === 0) continue;
    const t = state.levels.length > 1 ? i / (state.levels.length - 1) : 0;
    let d = '';
    for (let k = 0; k < segments.length; k += 4) {
      if (v.clip !== 'all') {
        const signed = ctx.field.distance(segments[k], segments[k + 1]);
        if (v.clip === 'outside' && signed < 0) continue;
        if (v.clip === 'inside' && signed > 0) continue;
      }
      d += `M${f(segments[k])} ${f(segments[k + 1])}L${f(segments[k + 2])} ${f(segments[k + 3])}`;
    }
    if (d) parts.push(`<path stroke="${state.palette.at(t)}" d="${d}"/>`);
  }

  parts.push('</g>');
  return parts.join('\n');
}
